import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './button';
import { Input } from './input';
import { toast } from 'sonner';
import { CopyIcon, CheckIcon, LinkIcon, UserAddIcon, CloseIcon } from './icons';

export const InviteDialog = ({ isOpen, onClose, projectId, joinCode }) => {
  const [copied, setCopied] = useState(false);

  const inviteLink = `${window.location.origin}/join/${projectId}?code=${joinCode}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success('Invite link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(joinCode);
      toast.success('Join code copied!');
    } catch (err) {
      toast.error('Failed to copy code');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-[var(--card)] rounded-[var(--radius-lg)] shadow-lg max-w-md w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-[var(--muted)]/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
                <UserAddIcon size={20} className="text-[var(--primary)]" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-[var(--fg)]">
                  Invite Collaborators
                </h3>
                <p className="text-xs text-[var(--fg)]/60">
                  Share this charter with your community
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-[var(--muted)] flex items-center justify-center transition-colors"
              data-testid="invite-close-button"
            >
              <CloseIcon size={16} className="text-[var(--fg)]/60" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Invite link */}
            <div>
              <label className="text-sm font-medium text-[var(--fg)] mb-2 block">
                Invite Link
              </label>
              <div className="flex gap-2">
                <Input
                  value={inviteLink}
                  readOnly
                  className="text-sm bg-[var(--muted)]/30"
                  data-testid="invite-link-input"
                />
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleCopyLink}
                  className="flex-shrink-0"
                  data-testid="invite-copy-link-button"
                >
                  {copied ? (
                    <CheckIcon size={16} />
                  ) : (
                    <CopyIcon size={16} />
                  )}
                </Button>
              </div>
            </div>

            {/* Join code */}
            <div>
              <label className="text-sm font-medium text-[var(--fg)] mb-2 block">
                Join Code
              </label>
              <div className="flex items-center gap-3">
                <div className="flex-1 font-mono text-2xl tracking-widest text-center py-3 bg-[var(--sky)] rounded-[var(--radius-sm)] border border-[var(--muted)]/50">
                  {joinCode}
                </div>
                <Button
                  variant="ghost"
                  size="md"
                  onClick={handleCopyCode}
                  data-testid="invite-copy-code-button"
                >
                  <CopyIcon size={16} />
                </Button>
              </div>
              <p className="text-xs text-[var(--fg)]/50 mt-2">
                Share this code with collaborators to join your charter project
              </p>
            </div>

            {/* Trust reminder */}
            <div className="bg-[var(--sky)]/50 rounded-[var(--radius-sm)] p-4">
              <div className="flex items-start gap-3">
                <LinkIcon size={20} className="text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[var(--fg)]">Trust as Currency</p>
                  <p className="text-xs text-[var(--fg)]/60 mt-1">
                    Only share this link with people you trust to co-create your community's founding charter.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};